const conf = require("../conf.json");

// Listings come from the API, not from Firestore.
//
// This app used to query Firestore directly from the player with a public API
// key. That was the single largest source of read operations on the
// realestate-data collection - one agency ran ~900 queries for ~99,000 reads in
// a day, because fetchProperties runs on every loadData, i.e. every time the
// media comes round in the playlist - and it had also gone stale: the importer
// writes realestate.listings in MySQL only, so the Firestore mirror stopped
// receiving new listings and the boards rendered a shrinking subset of what
// exists. See PLAN-api-migration.md.

// TTL matches the function's own Redis TTL. A shorter one here would only buy
// repeated cache hits upstream; a longer one would outlive the data the server
// is willing to vouch for.
const CACHE_TTL_MS = 10 * 60 * 1000;

// In-memory only, deliberately. A player restart should re-fetch: a stale board
// surviving a restart is worse than one extra request, and localStorage would
// make exactly that trade the wrong way.
const cache = new Map();
// Separate from `cache` so a burst of loadData calls collapses onto one request
// instead of firing N identical ones while the first is still in flight.
const inFlight = new Map();

// Lists travel comma-separated inside ONE query value, never as repeated keys.
// The gateway reduces URLSearchParams into a plain object, so
// ?agencies=a&agencies=b arrives at the function as {agencies:"b"} and the first
// value is gone without an error. Sorting is not cosmetic either: it is what
// lets two boards asking the same question share one server-side cache entry.
const listParam = (values) => [...new Set(values)].sort().join(",");

const cacheKeyFor = (agencies, types) =>
  `${listParam(agencies)}|${listParam(types)}`;

const requestUrl = (agencies, types) => {
  const params = new URLSearchParams({
    agencies: listParam(agencies),
    types: listParam(types),
  });
  return `${conf.api}?${params.toString()}`;
};

// getProperties returns every listing for the given agencies and types that has
// inspection times. The server applies `status = current` and the
// has-inspections filter itself, so what arrives is already what the board
// renders - the old code fetched ~110 listings per agency and threw most of them
// away on the client.
export const getProperties = async (agencies, types) => {
  if (!agencies || !agencies.length || !types || !types.length) return [];

  const key = cacheKeyFor(agencies, types);

  const hit = cache.get(key);
  if (hit && hit.expires > Date.now()) return hit.listings;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = (async () => {
    try {
      const response = await fetch(requestUrl(agencies, types));
      if (!response.ok) {
        throw new Error("Network response was not ok: " + response.statusText);
      }
      const body = await response.json();
      if (!body || body.status !== true || !Array.isArray(body.listings)) {
        throw new Error("Invalid listings response");
      }
      cache.set(key, {
        listings: body.listings,
        expires: Date.now() + CACHE_TTL_MS,
      });
      return body.listings;
    } catch (error) {
      console.error("Error fetching properties:", error);
      // Serve the last good answer rather than blanking the board over one bad
      // fetch: a screen showing ten-minute-old inspection times beats a screen
      // showing nothing.
      const stale = cache.get(key);
      return stale ? stale.listings : [];
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
};
